import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import { Brain, Sparkles, TrendingUp, AlertTriangle, Lightbulb, Send, Loader2 } from 'lucide-react';

const AIInsights = () => {
  const { api } = useAuth();
  const [predictions, setPredictions] = useState(null);
  const [query, setQuery] = useState('');
  const [insight, setInsight] = useState(null);
  const [loadingPredictions, setLoadingPredictions] = useState(true);
  const [loadingInsight, setLoadingInsight] = useState(false);

  useEffect(() => {
    fetchPredictions();
  }, []);

  const fetchPredictions = async () => {
    try {
      const response = await api.get('/ai/predictions');
      setPredictions(response.data);
    } catch (error) {
      console.error('AI Predictions error:', error);
      // Don't show error toast for AI - it's optional feature
      setPredictions({
        predicted_sales: 0,
        predicted_orders: 0,
        recommendations: ['Agrega más datos de ventas para obtener predicciones precisas'],
        trending_products: [],
        risk_alerts: []
      });
    } finally {
      setLoadingPredictions(false);
    }
  };

  const handleAskAI = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    
    setLoadingInsight(true);
    try {
      const response = await api.post('/ai/insights', { query });
      setInsight(response.data);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al consultar IA';
      if (errorMsg.includes('Budget')) {
        toast.error('Créditos de IA agotados. Ve a Perfil → Universal Key → Agregar Balance');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoadingInsight(false);
    }
  };

  const suggestedQueries = [
    '¿Cuáles son mis productos más rentables?',
    '¿Qué día de la semana tengo más ventas?',
    '¿Cómo puedo reducir el desperdicio de inventario?',
    '¿Qué promociones me recomiendas?'
  ];

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-20 md:pt-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-accent/10">
                <Brain className="w-6 h-6 text-accent" />
              </div>
              <h1 className="text-3xl font-chivo font-extrabold" data-testid="ai-insights-title">
                Inteligencia Artificial
              </h1>
            </div>
            <p className="text-muted-foreground">
              Predicciones y recomendaciones basadas en los datos de tu restaurante
            </p>
          </div>

          {/* AI Chat Section */}
          <Card className="mb-8 border border-border/50">
            <CardHeader>
              <CardTitle className="font-chivo flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-accent" />
                Consulta a la IA
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAskAI} className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Pregunta algo sobre tu negocio..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="flex-1"
                    data-testid="ai-query-input"
                  />
                  <Button 
                    type="submit" 
                    className="bg-accent text-white hover:bg-accent/90"
                    disabled={loadingInsight || !query.trim()}
                    data-testid="ai-submit-btn"
                  >
                    {loadingInsight ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {suggestedQueries.map((q, idx) => (
                    <Button
                      key={idx}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setQuery(q)}
                      className="text-xs"
                    >
                      {q}
                    </Button>
                  ))}
                </div>
              </form>

              {insight && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 bg-muted/30 rounded-lg"
                >
                  <h3 className="font-bold mb-2 flex items-center gap-2">
                    <Brain className="w-4 h-4 text-accent" />
                    Respuesta de IA
                  </h3>
                  <p className="text-sm whitespace-pre-wrap">{insight.insight}</p>
                </motion.div>
              )}
            </CardContent>
          </Card>

          {/* Predictions Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Sales Prediction */}
            <Card className="border border-border/50">
              <CardHeader>
                <CardTitle className="font-chivo flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-success" />
                  Predicción de Mañana
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingPredictions ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : predictions ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-success/10 rounded-lg">
                      <span className="text-sm">Ventas Estimadas</span>
                      <span className="text-2xl font-chivo font-extrabold text-success">
                        ${predictions.predicted_sales?.toLocaleString() || '---'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-accent/10 rounded-lg">
                      <span className="text-sm">Órdenes Estimadas</span>
                      <span className="text-2xl font-chivo font-extrabold text-accent">
                        {predictions.predicted_orders || '---'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    No hay datos suficientes para predicciones
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Trending Products */}
            <Card className="border border-border/50">
              <CardHeader>
                <CardTitle className="font-chivo flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-accent" />
                  Productos en Tendencia
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingPredictions ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : predictions?.trending_products?.length > 0 ? (
                  <div className="space-y-2">
                    {predictions.trending_products.map((product, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-muted/30 rounded">
                        <span className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center text-accent text-xs font-bold">
                          {idx + 1}
                        </span>
                        <span className="text-sm">{product}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Sin tendencias detectadas
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recommendations */}
          <Card className="mb-8 border border-border/50">
            <CardHeader>
              <CardTitle className="font-chivo flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-accent" />
                Recomendaciones
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingPredictions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : predictions?.recommendations?.length > 0 ? (
                <div className="grid md:grid-cols-3 gap-4">
                  {predictions.recommendations.map((rec, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="p-4 bg-muted/30 rounded-lg border border-border/50"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-accent flex items-center justify-center text-white text-xs font-bold">
                          {idx + 1}
                        </span>
                        <span className="font-medium text-sm">Recomendación</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{rec}</p>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  Sin recomendaciones disponibles
                </p>
              )}
            </CardContent>
          </Card>

          {/* Risk Alerts */}
          {predictions?.risk_alerts?.length > 0 && (
            <Card className="border-destructive/50 bg-destructive/5">
              <CardHeader>
                <CardTitle className="font-chivo flex items-center gap-2 text-destructive">
                  <AlertTriangle className="w-5 h-5" />
                  Alertas de Riesgo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {predictions.risk_alerts.map((alert, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-destructive/10 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0" />
                      <span className="text-sm">{alert}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default AIInsights;
